import React, { useState, useEffect } from 'react';
import {
  Receipt, AlertTriangle, CheckCircle, Loader2, FileText, Lock, TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { adminApi } from '@/lib/api';

const BillingPage = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [extra, setExtra] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = async () => {
    try {
      const res = await adminApi.getBilling();
      setData(res.data);
      if (res.data.locked_reports_count > 0) setExtra(String(res.data.locked_reports_count));
    } catch (err) {
      setError('Erro ao carregar faturação');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const unitPrice = data?.plan_addon_config?.extra_report_unit_price || 0;
  const qty = parseInt(extra || 0);
  const total = (qty * unitPrice).toFixed(2);

  const handleAccept = async () => {
    if (!qty || qty <= 0) { setError('Indique o número de denúncias extra'); return; }
    setAccepting(true);
    setError('');
    try {
      await adminApi.acceptAddon({ extra_reports: qty });
      setSuccess('Add-on aceite. Já pode tratar as denúncias em excesso.');
      setTimeout(() => setSuccess(''), 4000);
      setExtra('');
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao aceitar add-on');
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>;
  }
  if (!data) return null;

  const pct = data.unlimited ? 0 : Math.min(100, Math.round((data.reports_count / Math.max(1, data.effective_limit)) * 100));
  const needsAddon = data.overage_mode === 'require_confirmation' && data.locked_reports_count > 0;

  return (
    <div className="space-y-6" data-testid="admin-billing-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Faturação & Limites</h1>
        <p className="text-slate-500 mt-1">Consumo de denúncias do mês {data.month} e gestão de denúncias extra</p>
      </div>

      {error && <Alert className="bg-red-50 border-red-200"><AlertTriangle className="w-4 h-4 text-red-600" /><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>}
      {success && <Alert className="bg-emerald-50 border-emerald-200"><CheckCircle className="w-4 h-4 text-emerald-600" /><AlertDescription className="text-emerald-700">{success}</AlertDescription></Alert>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="billing-usage-card">
          <CardHeader className="pb-2"><CardDescription>Denúncias este mês</CardDescription>
            <CardTitle className="text-3xl">{data.reports_count}{!data.unlimited && <span className="text-lg text-slate-400"> / {data.effective_limit}</span>}</CardTitle>
          </CardHeader>
          <CardContent>
            {data.unlimited ? <span className="text-sm text-slate-500">Plano ilimitado</span> : (
              <div className="w-full bg-slate-100 rounded-full h-2">
                <div className={`h-2 rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Excesso</CardDescription>
            <CardTitle className={`text-3xl ${data.overage > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{data.overage}</CardTitle>
          </CardHeader>
          <CardContent><span className="text-sm text-slate-500">Add-ons confirmados: +{data.addon_extra}</span></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Modo ao exceder</CardDescription>
            <CardTitle className="text-lg">{data.overage_mode === 'require_confirmation' ? 'Exigir confirmação' : 'Só faturação'}</CardTitle>
          </CardHeader>
          <CardContent><span className="text-sm text-slate-500">Plano: <span className="capitalize">{data.plan}</span></span></CardContent>
        </Card>
      </div>

      {needsAddon && (
        <Card className="border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50" data-testid="addon-proposal-card">
          <CardContent className="py-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0"><Lock className="w-6 h-6 text-amber-600" /></div>
              <div className="flex-1">
                <h3 className="font-semibold text-amber-900 mb-1">Limite mensal excedido — {data.locked_reports_count} denúncia(s) bloqueada(s)</h3>
                <p className="text-sm text-amber-800 mb-4">
                  As denúncias continuam a ser recebidas, mas a sua gestão acima do limite está bloqueada. Para as tratar, aceite o custo adicional de um pacote de denúncias extra para este mês.
                </p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <Label className="text-xs text-slate-600">Denúncias extra</Label>
                    <Input type="number" value={extra} onChange={(e) => setExtra(e.target.value)}
                      className="w-32 bg-white" data-testid="addon-extra-input" />
                  </div>
                  <div className="text-sm text-slate-700">
                    {unitPrice > 0 ? <>Custo: <strong className="text-amber-700" data-testid="addon-total">{total}€</strong> <span className="text-slate-500">({unitPrice}€/denúncia)</span></> : <span className="text-slate-500">Sem custo unitário definido no plano</span>}
                  </div>
                  <Button onClick={handleAccept} disabled={accepting} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="accept-addon-btn">
                    {accepting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Aceitar custo e desbloquear
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {!needsAddon && data.overage > 0 && data.overage_mode === 'billing_only' && (
        <Alert className="bg-sky-50 border-sky-200">
          <TrendingUp className="w-4 h-4 text-sky-600" />
          <AlertDescription className="text-sky-700">
            Excedeu o limite em {data.overage} denúncia(s). As denúncias continuam a ser recebidas e o excesso será faturado. Não é necessária qualquer ação.
          </AlertDescription>
        </Alert>
      )}

      {!needsAddon && data.overage === 0 && (
        <Alert className="bg-emerald-50 border-emerald-200">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <AlertDescription className="text-emerald-700">Dentro do limite do plano este mês.</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default BillingPage;
